import React from 'react';

// The fields moved between: the text and number fields of the rows (not the dropdowns,
// whose arrows choose their values, nor the checkboxes)
const FIELDS =
  'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="file"]):not([disabled]):not([readonly])';

// Fields closer than this vertically are on the same line
const SAME_LINE_PX = 8;

const isVisible = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

// The searches of the dropdowns (react-select) use the arrows for their choices
const isSearchField = (element: HTMLElement): boolean =>
  element.id.startsWith('react-select') ||
  element.closest('.search-field') !== null;

const centerX = (rect: DOMRect) => rect.left + rect.width / 2;
const centerY = (rect: DOMRect) => rect.top + rect.height / 2;

// The same field of the rows, by its id: e.g. product-subproduct-3-quantity is the
// quantity of a material row
const ROW_FIELD_ID = /^(.*-)(\d+)(-[A-Za-z]+)$/;

/**
 * Up and down to the same field of the row above or below (in the order of the rows),
 * also when a row is shown on two lines in a narrow window
 */
const findSameFieldOfRow = (
  from: HTMLElement,
  fields: HTMLElement[],
  key: string
): HTMLElement | undefined => {
  const match = ROW_FIELD_ID.exec(from.id);
  if (match === null) {
    return undefined;
  }

  const [, prefix, , suffix] = match;
  const sameField = fields.filter((field) => {
    const other = ROW_FIELD_ID.exec(field.id);
    return other !== null && other[1] === prefix && other[3] === suffix;
  });
  const index = sameField.indexOf(from);
  return key === 'ArrowUp' ? sameField[index - 1] : sameField[index + 1];
};

/** The field to move to from a field with an arrow key, by where the fields are shown */
export const findArrowTarget = (
  from: HTMLElement,
  fields: HTMLElement[],
  key: string
): HTMLElement | undefined => {
  const origin = from.getBoundingClientRect();
  const others = fields
    .filter((field) => field !== from)
    .map((field) => ({ field, rect: field.getBoundingClientRect() }));

  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const sameLine = others.filter(
      ({ rect }) => Math.abs(centerY(rect) - centerY(origin)) < SAME_LINE_PX
    );
    const onSide = sameLine.filter(({ rect }) =>
      key === 'ArrowLeft'
        ? centerX(rect) < centerX(origin)
        : centerX(rect) > centerX(origin)
    );
    onSide.sort(
      (a, b) =>
        Math.abs(centerX(a.rect) - centerX(origin)) -
        Math.abs(centerX(b.rect) - centerX(origin))
    );
    return onSide[0]?.field;
  }

  // Up and down: the nearest line above or below, the field of the same column on it
  const lines = others.filter(({ rect }) =>
    key === 'ArrowUp'
      ? centerY(rect) < centerY(origin) - SAME_LINE_PX
      : centerY(rect) > centerY(origin) + SAME_LINE_PX
  );
  if (lines.length === 0) {
    return undefined;
  }

  const nearestY =
    key === 'ArrowUp'
      ? Math.max(...lines.map(({ rect }) => centerY(rect)))
      : Math.min(...lines.map(({ rect }) => centerY(rect)));
  const line = lines.filter(
    ({ rect }) => Math.abs(centerY(rect) - nearestY) < SAME_LINE_PX
  );
  line.sort(
    (a, b) =>
      Math.abs(centerX(a.rect) - centerX(origin)) -
      Math.abs(centerX(b.rect) - centerX(origin))
  );
  return line[0]?.field;
};

/**
 * Moving between the fields of the calculation window with the arrow keys: up and down
 * to the field of the same column on the row above or below, left and right to the next
 * field on the row when the cursor is already at the start or the end of the text.
 */
const handleArrowNavigation = (
  event: React.KeyboardEvent<HTMLElement>
): void => {
  const { key } = event;
  if (
    !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key) ||
    event.altKey ||
    event.ctrlKey ||
    event.metaKey ||
    event.shiftKey
  ) {
    return;
  }

  const from = event.target as HTMLElement;
  if (!(from instanceof HTMLInputElement) || !from.matches(FIELDS)) {
    return;
  }

  if (isSearchField(from)) {
    return;
  }

  // Within the text the cursor moves as usual
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const { selectionStart, selectionEnd, value } = from;
    // Number fields give no cursor: they move at once
    // A field moved to has its whole text chosen: the next arrow moves on
    const wholeTextChosen =
      selectionStart === 0 && selectionEnd === value.length && value.length > 0;
    if (selectionStart !== null && selectionEnd !== null && !wholeTextChosen) {
      if (selectionStart !== selectionEnd) {
        return;
      }
      if (key === 'ArrowLeft' && selectionStart > 0) {
        return;
      }
      if (key === 'ArrowRight' && selectionEnd < value.length) {
        return;
      }
    }
  }

  const fields = Array.from(
    event.currentTarget.querySelectorAll<HTMLElement>(FIELDS)
  ).filter((field) => isVisible(field) && !isSearchField(field));
  const target =
    (key === 'ArrowUp' || key === 'ArrowDown'
      ? findSameFieldOfRow(from, fields, key)
      : undefined) ?? findArrowTarget(from, fields, key);
  if (target === undefined) {
    return;
  }

  event.preventDefault();
  target.focus();
  if (target instanceof HTMLInputElement) {
    try {
      target.select();
    } catch (error) {
      // Some fields cannot be selected; the focus is enough
    }
  }
};

export default handleArrowNavigation;
